import {
  type ClipDevice,
  DEVICE_CATEGORIES,
  DEVICE_CATEGORY_LABELS,
  DEVICE_MODELS,
  deviceCanRotate,
  deviceFinish,
  deviceModel,
  devicePerspectives,
  matchingPerspective,
  type OverlayClip,
} from "@cva/shared";
import { Input, Select } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import type { AssetInfo } from "../commands";
import { useEditorApi } from "../store";
import { Row, Section, Segmented, Toggle } from "../ui";
import { defaultClipDevice, setOverlayDevice } from "./commands";

/**
 * "Gerät" in the properties of a clip on an upper track: shows the clip in a
 * phone, tablet, laptop, screen or browser window (screen recordings), with
 * colour, orientation and the ready-made 3D views.
 */
export function DeviceSection({
  clip,
  asset,
}: {
  clip: OverlayClip;
  asset: AssetInfo | undefined;
}) {
  const store = useEditorApi();
  const device = clip.device;
  const change = (next: ClipDevice | null, merge?: string) =>
    setOverlayDevice(store, clip.id, next, asset, merge);
  const patch = (values: Partial<ClipDevice>, merge?: string) => {
    if (device) change({ ...device, ...values }, merge);
  };
  const model = device ? deviceModel(device.device) : null;
  const finish = model && device ? deviceFinish(model, device.finish) : null;
  const perspective = device ? matchingPerspective(device.device, device.tilt) : null;
  return (
    <Section title="Gerät">
      <Toggle
        checked={Boolean(device)}
        onChange={(checked) => change(checked ? defaultClipDevice(asset) : null)}
        label="Im Gerät zeigen"
        description="Für Bildschirmaufnahmen: das Video läuft auf dem Bildschirm eines Handys, Laptops oder Browserfensters"
      />
      {device && model ? (
        <>
          <Select
            aria-label="Gerät"
            className="w-full"
            value={device.device}
            onChange={(event) => {
              const next = deviceModel(event.target.value);
              patch({
                device: next.id,
                finish: undefined,
                landscape: deviceCanRotate(next) ? device.landscape : false,
                // Every kind of device has its own views: start straight on.
                ...(next.spec.kind === model.spec.kind ? {} : { tilt: { x: 0, y: 0, z: 0 } }),
              });
            }}
          >
            {DEVICE_CATEGORIES.map((category) => (
              <optgroup key={category} label={DEVICE_CATEGORY_LABELS[category]}>
                {DEVICE_MODELS.filter((entry) => entry.category === category).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          {model.finishes.length > 1 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {model.finishes.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  title={entry.name}
                  aria-label={entry.name}
                  aria-pressed={finish?.id === entry.id}
                  onClick={() => patch({ finish: entry.id })}
                  className={cn(
                    "size-6 rounded-full border border-zinc-300 shadow-inner",
                    finish?.id === entry.id && "ring-2 ring-brand-500 ring-offset-1",
                  )}
                  style={{ background: entry.frame }}
                />
              ))}
              <span className="text-xs text-zinc-500">{finish?.name}</span>
            </div>
          ) : null}
          {deviceCanRotate(model) ? (
            <Segmented
              className="w-full"
              value={device.landscape ? "landscape" : "portrait"}
              onChange={(value) => patch({ landscape: value === "landscape" })}
              options={[
                { value: "portrait", label: "Hoch", title: "Hochformat" },
                { value: "landscape", label: "Quer", title: "Querformat" },
              ]}
            />
          ) : null}
          <div className="space-y-1">
            <span className="text-xs text-zinc-500">Ansicht</span>
            <div className="grid grid-cols-3 gap-1">
              {devicePerspectives(device.device).map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  aria-pressed={perspective === entry.id}
                  onClick={() => patch({ tilt: entry.tilt })}
                  className={cn(
                    "h-8 rounded-md border px-1 text-xs",
                    perspective === entry.id
                      ? "border-brand-500 bg-brand-50 text-brand-700"
                      : "border-zinc-200 text-zinc-700 hover:bg-zinc-50",
                  )}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          </div>
          <Toggle
            checked={device.glare}
            onChange={(glare) => patch({ glare })}
            label="Spiegelung"
            description="Ein sanfter Lichtreflex auf dem Glas"
          />
          {model.spec.kind === "browser" ? (
            <Row>
              <Input
                aria-label="Adresse in der Adresszeile"
                placeholder="Adresse, z. B. kaleido-lab.de"
                className="w-full"
                maxLength={200}
                value={device.url ?? ""}
                onChange={(event) =>
                  patch({ url: event.target.value || undefined }, `${clip.id}-url`)
                }
              />
            </Row>
          ) : null}
          <p className="text-xs text-zinc-500">
            Der Rahmen auf der Fläche ist das Gerät; „Bildausschnitt“ verschiebt das Video auf dem
            Bildschirm.
          </p>
        </>
      ) : null}
    </Section>
  );
}
